import { ReactNode } from "react";
import { Button, Radio, Select } from "antd";
import { DownOutlined, RightOutlined } from "@ant-design/icons";
import { ImportAmbiguousRowDto, ImportDecisionKind, ImportRowDto } from "../../../../domain/DataKitDto";
import {
  CardMode,
  CardResult,
  DecisionCard,
  DecisionState,
  IMPORT_RESOLUTION_CREATE,
  IMPORT_RESOLUTION_SKIP,
  KIND_ORDER,
  addAll,
  modeOf,
  skipAll,
} from "./dataKitImportChoices";

interface Props {
  cards: DecisionCard[];
  results: Map<string, CardResult>;
  state: DecisionState;
  onChange: (next: DecisionState) => void;
  open: Record<string, boolean>;
  /** Called with the card's current state, so the page needn't know which cards start open. */
  onToggle: (key: string, isOpen: boolean) => void;
  /** "Update “Skim Milk” in Dairy" - names the library item a choice would update. */
  candidateLabel: (id: string) => string;
  groupLabel: (row: ImportRowDto) => string;
  priceOf: (row: ImportRowDto) => string;
}

const SECTIONS: Record<ImportDecisionKind, { title: string; note: string }> = {
  KIT_MATCH: {
    title: "Matches more than one item",
    note: "These rows match more than one item in the library. Choose which item each row updates, or skip it.",
  },
  FILE_DUPLICATE: {
    title: "Same name in the file",
    note: "These rows have the same name in the same group. Choose whether to keep them as separate items or keep one.",
  },
  UPDATE_COLLISION: {
    title: "Same library item",
    note: "These rows would all update the same library item. Choose the row that updates it, or skip them.",
  },
  ARCHIVED_ID: {
    title: "Archived items",
    note: "An archived item in this library already uses these External IDs. Restore and update it, or skip the row.",
  },
  EXTERNAL_ID_CONFLICT: {
    title: "Shared External IDs",
    note: "These rows are skipped unless you import them. An imported row comes in as a new item; the first one keeps the External ID if nothing else in the library has it.",
  },
};

const tagsOf = (row: ImportRowDto) => row.tags || [];
const dietaryOf = (row: ImportRowDto) => row.dietary || [];

/** Chips for a list of labels, or "None". */
const chips = (labels: string[]) =>
  labels.length ? (
    labels.map((t) => (
      <span className="import-tag" key={t}>
        {t}
      </span>
    ))
  ) : (
    <span className="import-none">None</span>
  );

/** True when the rows disagree on a column - those columns are highlighted, as in the design. */
const differs = (rows: ImportRowDto[], read: (row: ImportRowDto) => string) =>
  new Set(rows.map(read)).size > 1;

/**
 * The "Needs a decision" tab. One card per decision, grouped by kind, each saying what will happen
 * to its rows - and nothing on it is dropped unless the member chose that (Oct 9, QA).
 */
export function DataKitImportDecisions({
  cards,
  results,
  state,
  onChange,
  open,
  onToggle,
  candidateLabel,
  groupLabel,
  priceOf,
}: Props) {
  if (cards.length === 0) {
    return <p className="import-tab-note">Nothing to decide — every row matched cleanly.</p>;
  }

  const setMode = (card: DecisionCard, mode: CardMode) =>
    onChange({ ...state, modes: { ...state.modes, [card.key]: mode } });
  const setKeep = (card: DecisionCard, rowNumber: number) =>
    onChange({
      ...state,
      modes: { ...state.modes, [card.key]: "one" },
      keep: { ...state.keep, [card.key]: rowNumber },
    });
  const setPick = (rowNumber: number, value: string) =>
    onChange({ ...state, picks: { ...state.picks, [rowNumber]: value } });

  const pickOptions = (a: ImportAmbiguousRowDto) => [
    ...a.candidateItemIds.map((id) => ({ value: id, label: candidateLabel(id) })),
    ...(a.canCreateSeparately
      ? [
          {
            value: IMPORT_RESOLUTION_CREATE,
            label: a.kind === "EXTERNAL_ID_CONFLICT" ? "Import the row" : "Add as a new item",
          },
        ]
      : []),
    { value: IMPORT_RESOLUTION_SKIP, label: "Skip this row" },
  ];

  const pickSelect = (a: ImportAmbiguousRowDto) => (
    <Select
      className="import-review-pick"
      size="small"
      placeholder="Choose an option"
      value={state.picks[a.row.rowNumber]}
      onChange={(value: string) => setPick(a.row.rowNumber, value)}
      options={pickOptions(a)}
      onClick={(e) => e.stopPropagation()}
    />
  );

  const pickLabel = (a: ImportAmbiguousRowDto) => {
    const pick = state.picks[a.row.rowNumber];
    if (!pick) return undefined;
    if (pick === IMPORT_RESOLUTION_SKIP) return "Skip";
    if (pick === IMPORT_RESOLUTION_CREATE) return "Add as a new item";
    return candidateLabel(pick);
  };

  /** The chip on a card's header: what will happen, or what it still needs. */
  const chipOf = (card: DecisionCard, result: CardResult): { label: string; cls: string } => {
    const mode = modeOf(card, state);
    switch (card.kind) {
      case "EXTERNAL_ID_CONFLICT": {
        const imported = card.rows.filter((a) => state.picks[a.row.rowNumber] === IMPORT_RESOLUTION_CREATE).length;
        if (imported === 0) return { label: "Skipped", cls: "skip" };
        return { label: imported === card.rows.length ? "Import all" : `Import ${imported} of ${card.rows.length}`, cls: "" };
      }
      case "FILE_DUPLICATE":
        if (mode === "skip") return { label: "Skip all", cls: "skip" };
        if (!result.resolved)
          return { label: mode === "one" && state.keep[card.key] === undefined ? "Choose a row" : "Choose an item", cls: "need" };
        return { label: mode === "one" ? "Keep one" : "Keep as separate items", cls: "" };
      case "UPDATE_COLLISION":
        if (mode === "skip") return { label: "Skip all", cls: "skip" };
        return result.resolved ? { label: "Keep one", cls: "" } : { label: "Choose a row", cls: "need" };
      default: {
        if (!result.resolved) return { label: "Choose an item", cls: "need" };
        const label = pickLabel(card.rows[0]) || "";
        return { label, cls: label === "Skip" ? "skip" : "" };
      }
    }
  };

  /** Description, tags, price and the rest side by side, the columns that differ highlighted. */
  const compareTable = (card: DecisionCard, leading?: (a: ImportAmbiguousRowDto) => ReactNode, trailing?: (a: ImportAmbiguousRowDto) => ReactNode, dim?: (a: ImportAmbiguousRowDto) => boolean) => {
    const rows = card.rows.map((a) => a.row);
    const cols: { title: string; read: (row: ImportRowDto) => string; render?: (row: ImportRowDto) => ReactNode }[] = [
      { title: "Description", read: (r) => r.description || "" },
      { title: "Tags", read: (r) => tagsOf(r).join("|"), render: (r) => chips(tagsOf(r)) },
      { title: "Price", read: priceOf },
    ];
    // Next to Tags, its own column (Oct 14, QA) - only when the file maps a Dietary / Allergen column.
    if (rows.some((r) => r.dietary != null)) {
      cols.splice(2, 0, { title: "Allergens", read: (r) => dietaryOf(r).join("|"), render: (r) => chips(dietaryOf(r)) });
    }
    if (rows.some((r) => r.extras)) cols.push({ title: "Extras", read: (r) => r.extras || "" });
    if (rows.some((r) => r.externalId)) cols.push({ title: "External ID", read: (r) => r.externalId || "" });
    if (new Set(rows.map(groupLabel)).size > 1) cols.unshift({ title: "Group", read: groupLabel });
    const diff = cols.map((c) => differs(rows, c.read));
    return (
      <div className="import-compare-wrap">
        <table className="import-compare">
          <thead>
            <tr>
              {leading && <th />}
              {cols.map((c, i) => (
                <th key={c.title} className={diff[i] ? "diff" : ""}>
                  {c.title}
                </th>
              ))}
              {trailing && <th>Result</th>}
            </tr>
          </thead>
          <tbody>
            {card.rows.map((a) => (
              <tr key={a.row.rowNumber} className={dim && dim(a) ? "dim" : ""}>
                {leading && <td className="pick">{leading(a)}</td>}
                {cols.map((c, i) => (
                  <td key={c.title} className={diff[i] ? "diff" : ""}>
                    {c.render ? c.render(a.row) : c.read(a.row) || <span className="import-none">—</span>}
                  </td>
                ))}
                {trailing && <td className="result">{trailing(a)}</td>}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  };

  /** What keeping a same-name row does. */
  const outcomeCell = (a: ImportAmbiguousRowDto) => {
    switch (a.outcome) {
      case "UPDATE":
        return <span>Updates the library item</span>;
      case "UP_TO_DATE":
        return <span className="import-none">Already in the library</span>;
      case "CHOOSE":
        return pickSelect(a);
      default:
        return <span>New item</span>;
    }
  };

  const body = (card: DecisionCard) => {
    const mode = modeOf(card, state);
    switch (card.kind) {
      case "FILE_DUPLICATE": {
        const kept = state.keep[card.key];
        return (
          <>
            <Radio.Group
              className="import-choices"
              optionType="button"
              size="small"
              value={mode}
              onChange={(e) => setMode(card, e.target.value)}
              options={[
                { label: "Keep as separate items", value: "separate" },
                { label: "Keep one", value: "one" },
                { label: "Skip all", value: "skip" },
              ]}
            />
            {compareTable(
              card,
              mode === "one"
                ? (a) => (
                    <Radio checked={kept === a.row.rowNumber} onChange={() => setKeep(card, a.row.rowNumber)} />
                  )
                : undefined,
              outcomeCell,
              (a) => mode === "skip" || (mode === "one" && kept !== undefined && kept !== a.row.rowNumber)
            )}
          </>
        );
      }
      case "UPDATE_COLLISION": {
        const kept = state.keep[card.key];
        return (
          <>
            <p className="import-review-reason">{card.rows[0].reason}</p>
            {compareTable(
              card,
              (a) => <Radio checked={mode === "one" && kept === a.row.rowNumber} onChange={() => setKeep(card, a.row.rowNumber)} />,
              undefined,
              (a) => mode === "skip" || (mode === "one" && kept !== a.row.rowNumber)
            )}
            <Button size="small" className="import-skip-card" onClick={() => setMode(card, "skip")}>
              Skip all
            </Button>
          </>
        );
      }
      case "EXTERNAL_ID_CONFLICT":
        return (
          <>
            <ul className="import-conflict-rows">
              {card.rows.map((a) => (
                <li key={a.row.rowNumber}>
                  <span className="import-conflict-text">
                    <span className="import-conflict-name">{a.row.name}</span>
                    <span className="import-conflict-group">{groupLabel(a.row)}</span>
                  </span>
                  <Select
                    className="import-review-pick"
                    size="small"
                    value={state.picks[a.row.rowNumber] || IMPORT_RESOLUTION_SKIP}
                    onChange={(value: string) => setPick(a.row.rowNumber, value)}
                    options={pickOptions(a)}
                  />
                </li>
              ))}
            </ul>
            <p className="import-review-reason error">{card.rows[0].reason}</p>
          </>
        );
      default:
        // KIT_MATCH, ARCHIVED_ID - one row, one pick.
        return (
          <>
            <p className="import-review-reason">{card.rows[0].reason}</p>
            {compareTable(card, undefined, pickSelect)}
          </>
        );
    }
  };

  const kinds = KIND_ORDER.filter((kind) => cards.some((c) => c.kind === kind));

  return (
    <div className="import-decisions">
      <div className="import-decide-bar">
        <p className="import-tab-note">
          Nothing on these cards is imported or skipped unless you choose it. Rows with the same
          name are kept as separate items unless you change that.
        </p>
        <div className="import-decide-all">
          {cards.some((c) => c.kind === "FILE_DUPLICATE") && (
            <Button size="small" onClick={() => onChange(addAll(cards, state))}>
              Add all
            </Button>
          )}
          <Button size="small" onClick={() => onChange(skipAll(cards, state))}>
            Skip all
          </Button>
        </div>
      </div>

      {kinds.map((kind) => (
        <div className="import-decide-section" key={kind}>
          {kinds.length > 1 && <h3>{SECTIONS[kind].title}</h3>}
          <p className="import-tab-note">{SECTIONS[kind].note}</p>
          {cards
            .filter((c) => c.kind === kind)
            .map((card) => {
              const result = results.get(card.key) as CardResult;
              const chip = chipOf(card, result);
              const isOpen = open[card.key] ?? (chip.cls === "need" || card.kind === "EXTERNAL_ID_CONFLICT");
              const first = card.rows[0].row;
              const title =
                card.kind === "EXTERNAL_ID_CONFLICT" ? `External ID ${first.externalId}` : first.name;
              const groups = Array.from(new Set(card.rows.map((a) => groupLabel(a.row)))).join(" · ");
              return (
                <div className={`import-review-card${chip.cls === "need" ? " need" : ""}`} key={card.key}>
                  <button
                    type="button"
                    className="import-review-card-head"
                    aria-expanded={isOpen}
                    onClick={() => onToggle(card.key, isOpen)}
                  >
                    <span className="import-review-info">
                      <span className="import-review-name">{title}</span>
                      <span className="import-review-meta">
                        {card.kind === "EXTERNAL_ID_CONFLICT"
                          ? `${card.rows.length} rows`
                          : `${groups} • ${card.rows.length} ${card.rows.length === 1 ? "row" : "rows"}`}
                      </span>
                    </span>
                    <span className={`import-chip ${chip.cls}`}>{chip.label}</span>
                    {isOpen ? <DownOutlined className="import-chev" /> : <RightOutlined className="import-chev" />}
                  </button>
                  {isOpen && <div className="import-review-card-body">{body(card)}</div>}
                </div>
              );
            })}
        </div>
      ))}
    </div>
  );
}
